import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { ChevronDown, ChevronUp, Mail, Save, RotateCcw, Send, CheckCircle2, AlertCircle, Lock, ShieldAlert } from "lucide-react";
import { usePersistedState } from "../hooks/usePersistedState";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SMTP settings panel — writes to `app_settings/smtp` so the config
// survives redeploys and can be edited without touching env vars.
export default function SmtpSettingsPanel() {
  const [collapsed, setCollapsed] = usePersistedState("ui.settings.smtp.collapsed", true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({
    host: "",
    port: 587,
    user: "",
    password: "",
    sender: "",
    use_tls: true,
    recipient: "",
    portal_url: "",
  });
  const [meta, setMeta] = useState({
    password_set: false,
    password_encrypted: null,
    config_source: null,
    updated_at: null,
    updated_by: null,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/access-request/smtp-settings`);
      setForm({
        host: data.host || "",
        port: data.port || 587,
        user: data.user || "",
        password: "", // never populate password field from server
        sender: data.sender || "",
        use_tls: data.use_tls !== false,
        recipient: data.recipient || "",
        portal_url: data.portal_url || "",
      });
      setMeta({
        password_set: !!data.password_set,
        password_encrypted: data.password_encrypted,
        config_source: data.config_source,
        updated_at: data.updated_at,
        updated_by: data.updated_by,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load SMTP settings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setField = (k) => (e) => {
    const v = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async () => {
    if (!form.host || !form.user) {
      toast.error("Host and user are required");
      return;
    }
    if (!meta.password_set && !form.password) {
      toast.error("Password is required on initial setup");
      return;
    }
    setSaving(true);
    try {
      // Only send password when the user typed one — empty means "keep existing".
      const payload = { ...form, port: Number(form.port) || 587 };
      if (!payload.password) delete payload.password;
      await axios.put(`${API}/access-request/smtp-settings`, payload);
      toast.success("SMTP settings saved");
      setForm((f) => ({ ...f, password: "" }));
      await load();
      // Nudge the access-requests panel to re-read its diagnostic strip.
      window.dispatchEvent(new Event("smtp-settings-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/access-request/test-email`);
      if (data.sent) {
        toast.success(`Test email sent to ${data.recipient}`, {
          description: `via ${data.host} · from ${data.sender}`,
        });
      } else {
        toast.error("Test email NOT sent", { description: data.reason || "unknown reason" });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const clearDb = async () => {
    if (!window.confirm("Clear the stored SMTP settings? The app will fall back to environment variables.")) return;
    try {
      await axios.delete(`${API}/access-request/smtp-settings`);
      toast.success("SMTP settings cleared");
      await load();
      window.dispatchEvent(new Event("smtp-settings-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear");
    }
  };

  const sourceLabel = meta.config_source === "db"
    ? "stored in database"
    : meta.config_source === "env"
    ? "using environment variables"
    : "not configured yet";

  return (
    <Card data-testid="smtp-settings-card">
      <CardHeader className="cursor-pointer" onClick={() => setCollapsed((c) => !c)}>
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Mail className="w-4 h-4" /> SMTP Settings
            <span className="text-xs font-normal text-muted-foreground ml-2">{sourceLabel}</span>
            {meta.password_set && (
              <span className="inline-flex items-center gap-1 ml-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="w-3.5 h-3.5" /> ready
              </span>
            )}
            {!meta.password_set && !loading && (
              <span className="inline-flex items-center gap-1 ml-1 text-xs font-medium text-amber-600 dark:text-amber-400">
                <AlertCircle className="w-3.5 h-3.5" /> needs setup
              </span>
            )}
          </span>
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </CardTitle>
      </CardHeader>
      {!collapsed && (
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground py-4">Loading…</p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground mb-4">
                These credentials are used to send access-request notification emails. Values saved here
                override the <span className="font-mono">SMTP_*</span> environment variables.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormRow label="SMTP host" required>
                  <Input
                    value={form.host}
                    onChange={setField("host")}
                    placeholder="smtp.namebrightmail.com"
                    data-testid="smtp-host-input"
                  />
                </FormRow>
                <FormRow label="Port" required hint="465 for SSL, 587 for STARTTLS">
                  <Input
                    type="number"
                    value={form.port}
                    onChange={setField("port")}
                    placeholder="587"
                    data-testid="smtp-port-input"
                  />
                </FormRow>
                <FormRow label="Username" required>
                  <Input
                    value={form.user}
                    onChange={setField("user")}
                    placeholder="mailer@yourdomain.com"
                    data-testid="smtp-user-input"
                  />
                </FormRow>
                <FormRow
                  label="Password"
                  required={!meta.password_set}
                  hint={meta.password_set ? "Leave blank to keep the current password" : undefined}
                >
                  <Input
                    type="password"
                    value={form.password}
                    onChange={setField("password")}
                    placeholder={meta.password_set ? "•••••••• (kept)" : "SMTP password"}
                    autoComplete="new-password"
                    data-testid="smtp-password-input"
                  />
                </FormRow>
                <FormRow label='"From" address' hint="Defaults to the username if blank">
                  <Input
                    value={form.sender}
                    onChange={setField("sender")}
                    placeholder="notifications@fecwatchdog.com"
                    data-testid="smtp-sender-input"
                  />
                </FormRow>
                <FormRow label="Use STARTTLS" hint="Only applies when port is not 465">
                  <div className="h-9 flex items-center gap-2">
                    <Switch
                      checked={form.use_tls}
                      onCheckedChange={(v) => setForm((f) => ({ ...f, use_tls: v }))}
                      data-testid="smtp-tls-switch"
                    />
                    <span className="text-xs text-muted-foreground">
                      {form.use_tls ? "STARTTLS enabled" : "STARTTLS disabled"}
                    </span>
                  </div>
                </FormRow>
                <FormRow label="Notification recipient" hint="Where access-request emails are delivered">
                  <Input
                    value={form.recipient}
                    onChange={setField("recipient")}
                    placeholder="you@yourdomain.com"
                    data-testid="smtp-recipient-input"
                  />
                </FormRow>
                <FormRow label="Portal URL" hint="Deep link included in email CTA">
                  <Input
                    value={form.portal_url}
                    onChange={setField("portal_url")}
                    placeholder="https://fecwatchdog.com/settings"
                    data-testid="smtp-portal-input"
                  />
                </FormRow>
              </div>

              {meta.updated_at && (
                <p className="text-[11px] text-muted-foreground mt-4 font-mono flex items-center gap-2">
                  {meta.password_encrypted ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                      <Lock className="w-3 h-3" /> encrypted at rest
                    </span>
                  ) : meta.password_encrypted === false ? (
                    <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                      <ShieldAlert className="w-3 h-3" /> stored as plaintext — save to encrypt
                    </span>
                  ) : null}
                  <span className="opacity-60">·</span>
                  <span>Last saved {new Date(meta.updated_at).toLocaleString()} by {meta.updated_by || "—"}</span>
                </p>
              )}

              <div className="flex flex-wrap gap-2 mt-5">
                <Button onClick={save} disabled={saving} data-testid="smtp-save-btn">
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                  {saving ? "Saving…" : "Save settings"}
                </Button>
                <Button
                  variant="outline"
                  onClick={sendTest}
                  disabled={testing || (!meta.password_set && !form.password)}
                  data-testid="smtp-test-btn"
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  {testing ? "Sending…" : "Send test email"}
                </Button>
                {meta.config_source === "db" && (
                  <Button variant="ghost" onClick={clearDb} data-testid="smtp-clear-btn">
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to env vars
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function FormRow({ label, required, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium flex items-center gap-1">
        {label}
        {required && <span className="text-rose-500">*</span>}
      </Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
